/**
 * Notification Templates API
 * Notification template management endpoints
 * NOTE: Matches backend at /v1/admin/notification-templates/*
 */

import apiClient from './client';
import type {
  NotificationTemplate,
  NotificationTemplateCreate,
  NotificationTemplateUpdate,
  ChannelInfo,
  ActionInfo,
  SendTestRequest,
  SendTestResponse,
} from '@/features/notification-templates/types';

export const notificationTemplatesApi = {
  /**
   * List all notification templates
   * @param includeInactive - Include inactive templates (default: false)
   */
  list: async (includeInactive = false): Promise<NotificationTemplate[]> => {
    const response = await apiClient.get('/notification-templates', {
      params: { include_inactive: includeInactive },
    });
    // Backend returns { templates: [...], total: number }
    return response.data.templates || [];
  },

  /**
   * Get a single notification template by name
   */
  get: async (name: string): Promise<NotificationTemplate> => {
    const response = await apiClient.get(`/notification-templates/${name}`);
    return response.data;
  },

  /**
   * Create a new notification template
   */
  create: async (data: NotificationTemplateCreate): Promise<NotificationTemplate> => {
    const response = await apiClient.post('/notification-templates', data);
    return response.data;
  },

  /**
   * Update an existing notification template
   */
  update: async (name: string, data: NotificationTemplateUpdate): Promise<NotificationTemplate> => {
    const response = await apiClient.put(`/notification-templates/${name}`, data);
    return response.data;
  },

  /**
   * Delete a notification template (system templates cannot be deleted)
   */
  delete: async (name: string): Promise<{ success: boolean }> => {
    const response = await apiClient.delete(`/notification-templates/${name}`);
    return response.data;
  },

  /**
   * Refresh the template cache
   */
  refreshCache: async (): Promise<{ success: boolean }> => {
    const response = await apiClient.post('/notification-templates/refresh-cache');
    return response.data;
  },

  /**
   * List all available notification channels
   */
  listChannels: async (): Promise<ChannelInfo[]> => {
    const response = await apiClient.get('/notification-templates/channels');
    return response.data;
  },

  /**
   * List all available notification actions
   */
  listActions: async (): Promise<ActionInfo[]> => {
    const response = await apiClient.get('/notification-templates/actions');
    return response.data;
  },

  /**
   * Send a test notification
   */
  sendTest: async (data: SendTestRequest): Promise<SendTestResponse> => {
    const response = await apiClient.post('/notification-templates/send-test', data);
    return response.data;
  },
};
